window.theme = window.theme || {};

(function setupLumeruneComponentAssets() {
  const themeScript = document.currentScript;
  const assetUrls = themeScript ? themeScript.dataset : {};
  theme.pageType = assetUrls.pageType;
  const pendingScripts = {};
  let swiperStylesheetPromise;

  function absoluteUrl(url) {
    return new URL(url, document.baseURI).href;
  }

  function findScript(url) {
    const targetUrl = absoluteUrl(url);
    return Array.from(document.scripts).find((script) => script.src === targetUrl);
  }

  function loadScriptOnce(key, url) {
    if (!url) return Promise.reject(new Error(`Missing ${key} asset URL`));
    if (pendingScripts[key]) return pendingScripts[key];

    const existingScript = findScript(url);
    if (existingScript) return Promise.resolve(existingScript);

    pendingScripts[key] = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = url;
      script.defer = true;
      script.dataset.lumeruneComponentAsset = key;
      script.addEventListener("load", () => resolve(script), { once: true });
      script.addEventListener("error", () => reject(new Error(`Unable to load ${key}`)), { once: true });
      document.head.appendChild(script);
    }).catch((error) => {
      delete pendingScripts[key];
      throw error;
    });

    return pendingScripts[key];
  }

  function ensureStylesheet(url) {
    if (!url) return Promise.reject(new Error("Missing Swiper stylesheet URL"));
    if (swiperStylesheetPromise) return swiperStylesheetPromise;

    const targetUrl = absoluteUrl(url);
    const existingStylesheet = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      .find((link) => link.href === targetUrl);

    if (existingStylesheet && existingStylesheet.sheet) {
      swiperStylesheetPromise = Promise.resolve(existingStylesheet);
      return swiperStylesheetPromise;
    }

    swiperStylesheetPromise = new Promise((resolve, reject) => {
      const link = existingStylesheet || document.createElement("link");
      link.addEventListener("load", () => resolve(link), { once: true });
      link.addEventListener("error", () => reject(new Error("Unable to load Swiper stylesheet")), { once: true });

      if (!existingStylesheet) {
        link.rel = "stylesheet";
        link.href = url;
        link.dataset.lumeruneComponentAsset = "swiper-stylesheet";
        document.head.appendChild(link);
      }
    }).catch((error) => {
      swiperStylesheetPromise = null;
      throw error;
    });

    return swiperStylesheetPromise;
  }

  window.lumeruneLoadSwiper = function loadSwiper() {
    const scriptPromise = window.Swiper
      ? Promise.resolve(window.Swiper)
      : loadScriptOnce("swiper", assetUrls.swiperScript);

    return Promise.all([ensureStylesheet(assetUrls.swiperStylesheet), scriptPromise]).then(() => {
      if (!window.Swiper) throw new Error("Swiper did not initialize");
      return window.Swiper;
    });
  };

  window.lumeruneProductViewUrl = function productViewUrl(handle, view) {
    const rootUrl = window.routes?.root_url || "/";
    const normalizedRoot = rootUrl.endsWith("/") ? rootUrl : `${rootUrl}/`;
    return `${normalizedRoot}products/${encodeURIComponent(handle)}?view=${encodeURIComponent(view)}`;
  };

  window.lumeruneLoadProductCardDependencies = function loadProductCardDependencies(root) {
    const scope = root && root.querySelector ? root : document;
    const contains = (selector) =>
      Boolean(scope.matches && scope.matches(selector)) || Boolean(scope.querySelector(selector));
    const hasQuickView = contains("quick-view-modal");
    const hasProductForm = hasQuickView || contains("product-form");
    const hasProductVariant = contains("variant-radios, variant-selects");
    const hasColorSwatch = contains("variant-swatch-buttons, color-swatch-variant");
    const hasRecentlyViewed = contains("recently-viewed-products");
    const tasks = [];

    if (hasProductForm && !customElements.get("product-form")) {
      tasks.push(loadScriptOnce("product-form", assetUrls.productFormScript));
    }
    if (hasProductVariant && !customElements.get("variant-radios")) {
      tasks.push(loadScriptOnce("product-variant", assetUrls.productVariantScript));
    }
    if (hasQuickView && !customElements.get("quick-view-modal")) {
      tasks.push(loadScriptOnce("quick-view", assetUrls.quickViewScript));
    }
    if (hasColorSwatch && !customElements.get("color-swatch-variant")) {
      tasks.push(loadScriptOnce("color-swatch", assetUrls.colorSwatchScript));
    }
    if (hasRecentlyViewed && !customElements.get("recently-viewed-products")) {
      tasks.push(loadScriptOnce("recently-viewed", assetUrls.recentlyViewedScript));
    }

    return Promise.all(tasks).catch((error) => {
      console.error("LUMERUNE component asset error:", error);
    });
  };

  document.addEventListener("shopify:section:load", (event) => {
    window.lumeruneLoadProductCardDependencies(event.target);
  });
})();

theme.SlideShow = (function () {
  function Slider(element) {
    window.lumeruneLoadSwiper()
      .then(() => {
        if (!element.isConnected) return;

        const slideshow = element.querySelector(".slideshow__wrapper");
        const paginationSelector = element.querySelector(".swiper-pagination");
        const navigationPrevious = element.querySelector(".swiper-button-prev");
        const navigationNext = element.querySelector(".swiper-button-next");
        const autoplayEnabled = element.dataset.sliderAutoplay === "true";
        const autoplayDelay = parseInt(element.dataset.sliderDelay, 10);
        const loopEnabled = element.dataset.sliderLoop === "true";
        const paginationType = element.dataset.pagination === "counter" ? "fraction" : "bullets";

        if (!slideshow) return;

        if (element.lumeruneSwiper && typeof element.lumeruneSwiper.destroy === "function") {
          element.lumeruneSwiper.destroy(true, true);
        }

        element.lumeruneSwiper = new Swiper(slideshow, {
          loop: loopEnabled,
          pagination: {
            el: paginationSelector,
            clickable: true,
            type: paginationType,
            renderBullet: function (index, className) {
              return `<span class="${className}">${index + 1}</span>`;
            },
          },
          autoplay: autoplayEnabled ? { delay: autoplayDelay } : false,
          autoHeight: true,
          navigation: {
            nextEl: navigationNext,
            prevEl: navigationPrevious,
          },
        });
      })
      .catch((error) => console.error("LUMERUNE slideshow error:", error));
  }

  return Slider;
})();

if (!customElements.get("menu-slider")) {
  customElements.define(
    "menu-slider",
    class MenuSlider extends HTMLElement {
      constructor() {
        super();
        const element = this;
        const slider = this.querySelector(".menu-product-active");
        if (!slider || this.dataset.sliderEnable !== "true") return;

        window.lumeruneLoadSwiper()
          .then(() => {
            if (!element.isConnected) return;

            const autoplayEnabled = element.dataset.sliderAutoplay === "true";
            const autoplayDelay = parseInt(element.dataset.autoplayTime, 10);
            if (element.swiper && typeof element.swiper.destroy === "function") {
              element.swiper.destroy(true, true);
            }

            element.swiper = new Swiper(slider, {
              loop: element.dataset.sliderLoop === "true",
              slidesPerView: parseInt(element.dataset.showMobile, 10),
              spaceBetween: 30,
              autoplay: autoplayEnabled ? { delay: autoplayDelay } : false,
              pagination: {
                el: element.querySelector(".swiper-pagination"),
                clickable: true,
                type: "fraction",
              },
              navigation: {
                nextEl: element.querySelector(".swiper-button-next"),
                prevEl: element.querySelector(".swiper-button-prev"),
              },
              breakpoints: {
                640: { slidesPerView: parseInt(element.dataset.showMobile, 10) },
                750: { slidesPerView: parseInt(element.dataset.showExtraLarge, 10) },
              },
            });
          })
          .catch((error) => console.error("LUMERUNE menu slider error:", error));
      }
    }
  );
}

theme.accordion = (function () {
  function Accordion(section) {
    section.querySelectorAll(".faq__list--item__button").forEach((button) => {
      if (button.dataset.lumeruneAccordionReady === "true") return;
      button.dataset.lumeruneAccordionReady = "true";

      const item = button.closest(".faq__list--item");
      const body = item && item.querySelector(".faq__body");
      if (!item || !body) return;

      item.addEventListener("keyup", (event) => {
        if (event.code !== "Escape") return;
        item.classList.remove("active");
        button.setAttribute("aria-expanded", "false");
        slideUp(body);
        removeTrapFocus(item);
      });

      button.addEventListener("click", () => {
        const willOpen = !item.classList.contains("active");
        item.classList.toggle("active", willOpen);
        button.setAttribute("aria-expanded", String(willOpen));

        if (willOpen) {
          slideDown(body);
          item.addEventListener("transitionend", () => trapFocus(item), { once: true });
          getSiblings(item).forEach((sibling) => {
            const siblingBody = sibling.querySelector(".faq__body");
            const siblingButton = sibling.querySelector(".faq__list--item__button");
            sibling.classList.remove("active");
            siblingButton?.setAttribute("aria-expanded", "false");
            if (siblingBody) slideUp(siblingBody);
          });
        } else {
          slideUp(body);
        }
      });
    });
  }

  return Accordion;
})();

theme.blog = (function () {
  function FeaturedBlog(section) {
    const content = section.querySelector(".blog--item-1 .article-card__info");
    const imageWrapper = section.querySelector(".blog--item-1 .media.media--landscape");
    if (!content || !imageWrapper) return;

    imageWrapper.style.height = `calc(100% - ${content.clientHeight}px)`;
  }

  return FeaturedBlog;
})();

theme.Sections = function Sections() {
  this.constructors = {};
  this.instances = [];

  document.addEventListener(
    "shopify:section:load",
    this._onSectionLoad.bind(this)
  );
  document.addEventListener(
    "shopify:section:unload",
    this._onSectionUnload.bind(this)
  );
  document.addEventListener(
    "shopify:section:select",
    this._onSelect.bind(this)
  );
  document.addEventListener(
    "shopify:section:deselect",
    this._onDeselect.bind(this)
  );
  document.addEventListener(
    "shopify:block:select",
    this._onBlockSelect.bind(this)
  );
  document.addEventListener(
    "shopify:block:deselect",
    this._onBlockDeselect.bind(this)
  );
};

theme.Sections.prototype = Object.assign({}, theme.Sections.prototype, {
  _createInstance: function (container, constructor) {
    var id = container.getAttribute("data-section-id");
    var type = container.getAttribute("data-section-type");

    constructor = constructor || this.constructors[type];

    if (typeof constructor === "undefined") {
      return;
    }

    var instance = Object.assign(new constructor(container), {
      id: id,
      type: type,
      container: container,
    });

    this.instances.push(instance);
  },

  _onSectionLoad: function (evt) {
    var container = document.querySelector(
      '[data-section-id="' + evt.detail.sectionId + '"]'
    );

    if (container) {
      this._createInstance(container);
    }
  },

  _onSectionUnload: function (evt) {
    this.instances = this.instances.filter(function (instance) {
      var isEventInstance = instance.id === evt.detail.sectionId;

      if (isEventInstance) {
        if (typeof instance.onUnload === "function") {
          instance.onUnload(evt);
        }
        if (
          instance.container.lumeruneSwiper &&
          typeof instance.container.lumeruneSwiper.destroy === "function"
        ) {
          instance.container.lumeruneSwiper.destroy(true, true);
        }
      }

      return !isEventInstance;
    });
  },

  _onSelect: function (evt) {
    // eslint-disable-next-line no-shadow
    var instance = this.instances.find(function (instance) {
      return instance.id === evt.detail.sectionId;
    });

    if (
      typeof instance !== "undefined" &&
      typeof instance.onSelect === "function"
    ) {
      instance.onSelect(evt);
    }
  },

  _onDeselect: function (evt) {
    // eslint-disable-next-line no-shadow
    var instance = this.instances.find(function (instance) {
      return instance.id === evt.detail.sectionId;
    });

    if (
      typeof instance !== "undefined" &&
      typeof instance.onDeselect === "function"
    ) {
      instance.onDeselect(evt);
    }
  },

  _onBlockSelect: function (evt) {
    // eslint-disable-next-line no-shadow
    var instance = this.instances.find(function (instance) {
      return instance.id === evt.detail.sectionId;
    });

    if (
      typeof instance !== "undefined" &&
      typeof instance.onBlockSelect === "function"
    ) {
      instance.onBlockSelect(evt);
    }
  },

  _onBlockDeselect: function (evt) {
    // eslint-disable-next-line no-shadow
    var instance = this.instances.find(function (instance) {
      return instance.id === evt.detail.sectionId;
    });

    if (
      typeof instance !== "undefined" &&
      typeof instance.onBlockDeselect === "function"
    ) {
      instance.onBlockDeselect(evt);
    }
  },

  register: function (type, constructor) {
    this.constructors[type] = constructor;

    document.querySelectorAll('[data-section-type="' + type + '"]').forEach(
      function (container) {
        this._createInstance(container, constructor);
      }.bind(this)
    );
  },
});

theme.collectionSlider = (function () {
  function sliderProduct(e) {
    function initializeSlider() {
      if (e.dataset.sliderEnable !== "true") return;

      const sliderContainer = e.querySelector(".productSlider");
      if (!sliderContainer || !sliderContainer.querySelector(".swiper-slide")) return;

      if (e.lumeruneSwiper) {
        e.lumeruneSwiper.update();
        return;
      }
      if (e.lumeruneSwiperLoading) return;

      const extraLargeDesktopShow = parseInt(e.dataset.showExtraLarge, 10) || 4;
      const largeDesktopShow = 3;
      const mobileShow = parseInt(e.dataset.showMobile, 10) || 2;
      const slideCount = sliderContainer.querySelectorAll(".swiper-slide").length;
      const sliderAutoplay = e.dataset.autoplay;
      const sliderLoop = e.dataset.sliderLoop;
      const autoPlayTime = parseInt(e.dataset.autoplayTime, 10);
      const rowGap = Number(e.dataset.rowSpace) || 30;
      const autoPlayValue = sliderAutoplay === "true" ? { delay: autoPlayTime } : false;
      const loopRequested = sliderLoop ? sliderLoop.trim() === "true" : true;
      const loopEnabled = loopRequested && slideCount > Math.max(extraLargeDesktopShow, largeDesktopShow, mobileShow);

      e.lumeruneSwiperLoading = window.lumeruneLoadSwiper()
        .then(() => {
          if (!e.isConnected || e.lumeruneSwiper) return;

          e.lumeruneSwiper = new Swiper(sliderContainer, {
            loop: loopEnabled,
            slidesPerView: mobileShow,
            spaceBetween: 20,
            autoplay: autoPlayValue,
            pagination: {
              el: e.querySelector(".swiper-pagination"),
              clickable: true,
              type: "fraction",
            },
            navigation: {
              nextEl: e.querySelector(".swiper-button-next"),
              prevEl: e.querySelector(".swiper-button-prev"),
            },
            breakpoints: {
              640: {
                slidesPerView: mobileShow,
                spaceBetween: 20,
              },
              750: {
                slidesPerView: largeDesktopShow,
                spaceBetween: 20,
              },
              992: {
                slidesPerView: extraLargeDesktopShow,
                spaceBetween: 20,
              },
              1200: {
                slidesPerView: extraLargeDesktopShow,
                spaceBetween: rowGap,
              },
            },
          });

          if (sliderAutoplay === "true") {
            sliderContainer.addEventListener("mouseenter", () => {
              e.lumeruneSwiper.autoplay.stop();
            });
            sliderContainer.addEventListener("mouseleave", () => {
              e.lumeruneSwiper.autoplay.start();
            });
          }
        })
        .catch((error) => console.error("LUMERUNE product slider error:", error))
        .finally(() => {
          e.lumeruneSwiperLoading = null;
        });
    }

    e.lumeruneRefreshProductSlider = initializeSlider;
    initializeSlider();
  }
  return sliderProduct;
})();

document.addEventListener("DOMContentLoaded", function () {
  if (theme.pageType === 'product') {
    const productSection = document.querySelector('[data-section-type="main-product"]');
    if (productSection) window.lumeruneLoadProductCardDependencies(productSection);
    const selector = 'quick-view-modal, variant-swatch-buttons, color-swatch-variant';
    const prepareCard = (event) => {
      const component = event.target.closest?.(selector);
      if (!component || customElements.get(component.localName)) return;
      window.lumeruneLoadProductCardDependencies(component);
    };
    document.addEventListener('pointerover', prepareCard, { passive: true });
    document.addEventListener('focusin', prepareCard);
    document.addEventListener('click', (event) => {
      const component = event.target.closest?.(selector);
      if (!component || customElements.get(component.localName)) return;
      const target = event.target.closest('button, a, input, label') || event.target;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (component.dataset.lumeruneLoading === 'true') return;
      component.dataset.lumeruneLoading = 'true';
      window.lumeruneLoadProductCardDependencies(component).then(() => {
        if (component.isConnected && customElements.get(component.localName)) target.click();
      }).finally(() => delete component.dataset.lumeruneLoading);
    }, true);
  } else {
    window.lumeruneLoadProductCardDependencies(document);
  }

  let sections = new theme.Sections(),
    headerSearchModule = new theme.Sections(),
    headerCartModule = new theme.Sections(),
    headerStickyModule = new theme.Sections(),
    headerMainMenu = new theme.Sections();

  sections.register("scrolling_bar", theme.scrollingText);
  sections.register("header", theme.headerSection);
  headerSearchModule.register("header", theme.headerSearch);
  headerCartModule.register("header", theme.CartDrawerActions);
  headerStickyModule.register("header", theme.headerSticky);
  headerMainMenu.register("header", theme.headerMainMenuModule);
  sections.register("footer", theme.footerSection);
  sections.register("slideShow", theme.SlideShow);
  sections.register("collection-product", theme.collectionProduct);
  sections.register("product-slider", theme.collectionSlider);
  sections.register("accordion", theme.accordion);
  sections.register("blog", theme.blog);
});

